import { Component, HostListener, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { AddDestinationAlternateModalComponent } from '@components/add-destination-alternate-modal/add-destination-alternate-modal.component';
import { ImmutabilityDisableModalComponent } from '@components/immutability-modals/immutability-disable-modal/immutability-disable-modal.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { FormsUtil, MbsPopupType, MbsSize, ModalComponent, ModalService, ToastService } from 'mbs-ui-kit';
import { PermissionsEnum, StorageType } from 'mbs-ui-shared';
import { ftpStorageTypes, getBucketTypeName } from 'mbs-ui-shared@constants/storage.constants';
import { BucketInfo, Destination, GoogleStorageClassEnum, SetDestinationType, StorageAccountV2 } from 'mbs-ui-shared@models/StorageAccount';
import { ImmutabilityConfirmModalComponent } from 'mbs-ui-shared@modules/immutability-modals/immutability-confirm-modal/immutability-confirm-modal.component';
import { StorageAccountsService } from 'mbs-ui-shared@services/storage-accounts/storage-accounts.service';
import { BehaviorSubject, Observable, from, noop, of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import {
  AddDestinationPartialSuccessModalComponent
} from '@components/add-destination-partial-success-modal/add-destination-partial-success-modal.component';

@UntilDestroy()
@Component({
  selector: 'mbs-add-destination-bucket-modal',
  templateUrl: './add-destination-modal.component.html',
  styleUrls: ['./add-destination-modal.component.scss']
})
export class AddDestinationModalComponent implements OnInit {
  public readonly popupType = MbsPopupType;
  public readonly storageTypeEnum = StorageType;
  public deprecatedStorageTypes = [StorageType.OracleCloud];

  public storageAccount: StorageAccountV2;
  public currentBucket: BucketInfo;
  public destination: Destination;
  public loading = true;
  public innerLoading = false;
  public form = this.fb.group({
    displayName: new FormControl('', [Validators.required]),
    bucketName: new FormControl('', [Validators.required]),
    region: new FormControl(null),
    immutability: new FormControl(null),
    storageClass: new FormControl(null),
    googleDestinationSubregion: new FormControl(null),
    companies: new FormControl([]),
    s3Accelerator: new FormControl(null),
    reuseAccountRules: new FormControl(null),
    isExternalBucket: new FormControl(null)
  });
  public finalSuccessAction: () => void;
  public createNew = new FormControl(true);
  public errorText = null;
  public bucketTypeName = null;
  public hasCriticalError = false;
  public editDestinationButtonLoading = new BehaviorSubject(false);

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  @HostListener('window:message', ['$event'])
  handleMessage(event: MessageEvent): void {
    const { msg, data: credential } = event.data;

    if (msg === 'add-destination-modal') {
      this.applyAction(credential);
    }
  }

  constructor(
    private fb: FormBuilder,
    private storageAccountService: StorageAccountsService,
    private toast: ToastService,
    private i18N: I18NextPipe,
    private modalService: ModalService,
    private ability: AbilityService
  ) {}

  ngOnInit() {
    if (this.deprecatedStorageTypes.includes(this.storageAccount.storageType)) {
      this.loading = false;
    }

    this.bucketTypeName = getBucketTypeName(this.storageAccount.storageType, this.i18N);
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => !this.hasCriticalError && (this.errorText = null));
  }

  public getModalTitle(): string {
    return this.destination
      ? this.i18N.transform('shared:modals.addDestinationBucket.edit')
      : this.i18N.transform('shared:modals.addDestinationBucket.create');
  }

  public onActionButtonClick() {
    if (!this.form.valid) {
      return void FormsUtil.triggerValidation(this.form);
    }

    if (this.form.get('reuseAccountRules')?.value) {
      return void window.open(
        '/AP/Azure/OAuth?action=add-destination-modal',
        'Azure OAuth 2.0',
        'width=500,height=500,toolbar=0,menubar=0,location=0,status=1,scrollbars=1,resizable=1,left=0,top=0'
      );
    }

    this.applyAction();
  }

  public onCriticalError(error: string) {
    if (!error) return;

    this.hasCriticalError = true;
    this.errorText = error;
  }

  private showImmutabilityModal(): Observable<{ confirm: boolean }> {
    const modal = this.destination?.immutabilityEnabled ? ImmutabilityDisableModalComponent : ImmutabilityConfirmModalComponent;

    return from(this.modalService.openCustom<{ confirm: boolean }>(modal));
  }

  private handleFinalSuccess() {
    this.toast.success(this.i18N.transform('shared:modals.addDestinationBucket.destinationSaved'));
    this.finalSuccessAction && this.finalSuccessAction();
  }

  private editDestination(destinationId: Destination['id']) {
    this.editDestinationButtonLoading.next(true);
    this.storageAccountService
      .getDestination(this.storageAccount.id, destinationId)
      .pipe(finalize(() => this.editDestinationButtonLoading.next(false)))
      .subscribe((destination) => {
        this.modalService.dismissAll();
        const modal = ftpStorageTypes.includes(this.storageAccount.storageType)
          ? AddDestinationAlternateModalComponent
          : AddDestinationModalComponent;

        const modalInstance = this.modalService.openRef(modal, {
          size: MbsSize.md
        });
        modalInstance.componentInstance.storageAccount = this.storageAccount;
        modalInstance.componentInstance.destination = destination;

        modalInstance.result
          .then(() => {
            this.finalSuccessAction && this.finalSuccessAction();
          })
          .catch(noop);
      });
  }

  private needToShowImmutabilityChangedModal() {
    return (
      this.destination &&
      this.storageAccount?.immutabilitySupported &&
      !this.createNew?.value &&
      this.ability.can('read', PermissionsEnum.ManageImmutability) &&
      this.destination?.immutabilityEnabled !== this.form?.value?.immutability
    );
  }

  private prepareData(credential?: { code: string; redirectUri: string }): SetDestinationType {
    const createNew = this.createNew.value;
    const formValue = this.form.value;
    const supportedStorageClassRegion =
      formValue?.storageClass?.id === GoogleStorageClassEnum.MultiRegional ? formValue?.region : formValue?.googleDestinationSubregion;

    const region = createNew
      ? this.storageAccount?.supportedStorageClass
        ? supportedStorageClassRegion
        : formValue.region
      : this.currentBucket?.region;

    return {
      displayBucketName: formValue.displayName,
      immutabilityEnabled: formValue.immutability,
      createNewBucket: createNew,
      googleStorageClass: createNew ? formValue.storageClass?.id : null,
      assignToCompanies: formValue.companies.map((company) => company.id),
      isExternalBucket: formValue.isExternalBucket,
      bucket: {
        bucketName: createNew ? formValue.bucketName : this.currentBucket?.bucketName,
        region,
        s3AcceleratorEnabled: formValue.s3Accelerator,
        cloudBucketID: createNew ? null : this.currentBucket?.cloudBucketID,
        immutabilitySupported: createNew ? formValue.immutability : this.currentBucket?.immutabilitySupported
      },
      credential
    };
  }

  private handlePartialSuccess(res: { extensions: { [key: string]: string }; instance: string }) {
    this.baseModal.close();
    const modal = this.modalService.openRef(AddDestinationPartialSuccessModalComponent);
    modal.componentInstance.extensions = Object.values(res.extensions);

    modal.result.then((edit) => {
        edit && this.editDestination(res.instance);
      })
      .catch(() => {
        this.handleFinalSuccess();
      });
  }

  private applyAction(credential?: { code: string; redirectUri: string }) {
    this.innerLoading = true;

    const data: SetDestinationType = this.prepareData(credential);

    const preparation$ = this.needToShowImmutabilityChangedModal() ? this.showImmutabilityModal() : of(null);

    const query$ = this.destination
      ? this.storageAccountService.editDestination({
          storageAccountId: this.storageAccount.id,
          data,
          destinationId: this.destination?.id,
          skipError: true
        })
      : this.storageAccountService.createDestination({
          storageAccountId: this.storageAccount.id,
          data,
          skipError: true
        });

    preparation$
      .pipe(
        switchMap((res) => {
          if (res && !res?.confirm) {
            throw new Error();
          } else {
            return query$;
          }
        }),
        finalize(() => (this.innerLoading = false))
      )
      .subscribe({
        next: (res) => {
          if (res) {
            this.handlePartialSuccess(res);
          } else {
            this.handleFinalSuccess();
          }
          this.baseModal.save();
        },
        error: (err) => (this.errorText = err?.error?.detail)
      });
  }
}
